import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { verifyCharityStripeAccount, deleteCharity as apiDeleteCharity } from '../services/charityService';
import { getPledgesByCharityId } from '../services/pledgeService';
import { getUsers } from '../services/dataService';
import { getCachedData, clearCachedData } from '../services/cacheService';
import LoadingSpinner from './common/LoadingSpinner';
import ConfirmModal from './common/ConfirmModal';

const CharityView = () => {
  const { charityId } = useParams();
  useContext(AuthContext);
  const navigate = useNavigate();
  const [charity, setCharity] = useState(null);
  // Optional override account id used by the "Verify Stripe Account" action.
  const [overrideAccountId, setOverrideAccountId] = useState('');
  const [pledges, setPledges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Load users data to cache for name lookups
        await getUsers();

        const { charity: charityData, pledges: charityPledges } = await getPledgesByCharityId(charityId);
        if (!charityData) {
          setError('Charity not found.');
          setCharity(null);
          setPledges([]);
        } else {
          setCharity(charityData);
          setPledges(charityPledges);
        }
      } catch (err) {
        console.error('Error fetching charity data:', err);
        setError('Failed to load charity data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    if (charityId) {
      loadData();
    }
  }, [charityId]);

  // Re-run Stripe Connect verification. With no override the backend re-checks
  // the stored account; with an override it verifies (and persists) the new id.
  const handleVerifyStripe = async () => {
    setVerifying(true);
    setError(null);
    setSuccessMessage('');
    try {
      const result = await verifyCharityStripeAccount(
        charityId,
        overrideAccountId.trim() || undefined
      );
      // Reflect the persisted verification result in local state.
      setCharity(prev => ({
        ...prev,
        stripe_account_id: result.stripe_account_id ?? prev.stripe_account_id,
        is_stripe_verified: result.is_stripe_verified ?? result.valid,
        stripe_charges_enabled: result.charges_enabled,
        stripe_last_verified_at: result.stripe_last_verified_at ?? new Date().toISOString(),
      }));
      clearCachedData('charities');
      setSuccessMessage(
        result.charges_enabled
          ? 'Stripe account verified — charges enabled. Charity is pledgeable.'
          : 'Verification ran, but Stripe reports charges are NOT enabled. Charity is not yet pledgeable.'
      );
      setOverrideAccountId('');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error verifying Stripe account:', err);
      const message = (err.message || '').includes('STRIPE_ACCOUNT_INVALID')
        ? 'Stripe rejected this account (not found or charges disabled). Check the account ID.'
        : ('Failed to verify Stripe account. ' + (err.message || ''));
      setError(message);
    } finally {
      setVerifying(false);
    }
  };

  const handleDeleteCharity = () => {
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      await apiDeleteCharity(charityId);
      clearCachedData('charities');
      navigate('/');
    } catch (err) {
      console.error('Error deleting charity:', err);
      setError('Failed to delete charity.');
      setLoading(false);
    } finally {
      setShowConfirmModal(false);
    }
  };

  // Helper function to lookup user name from cache
  const getUserNameById = (userId) => {
    const cachedUsers = getCachedData('users');
    if (cachedUsers && cachedUsers.data && cachedUsers.data.users) {
      const u = cachedUsers.data.users.find(item => item.id === userId);
      return u ? u.name : '';
    }
    return '';
  };

  if (loading && !charity) return <div className="flex justify-center items-center h-screen"><LoadingSpinner /></div>;
  if (error && !charity) return <div className="container mx-auto p-4 text-red-500 text-center">{error}</div>;
  if (!charity) return <div className="container mx-auto p-4 text-center">Charity not found.</div>;

  const isPledgeable = Boolean(charity.is_stripe_verified && charity.stripe_charges_enabled);

  const pledgeColumns = [
    {
      key: 'user_id',
      header: 'User Name',
      render: (userId) => getUserNameById(userId) || '',
    },
    { key: 'amount_cents', header: 'Amount', render: (val) => `€${(val / 100).toFixed(2)}` },
    { key: 'status', header: 'Status' },
  ];

  return (
    <div className="container mx-auto p-4 sm:p-6 lg:p-8">
      <div className="bg-white shadow-xl rounded-lg p-6 md:p-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 pb-4 border-b border-gray-200">
          <h1 className="text-3xl font-bold text-gray-800">{charity.name}</h1>
          <button
            onClick={handleDeleteCharity}
            disabled={loading || verifying}
            className="mt-4 sm:mt-0 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition duration-150 ease-in-out disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Delete Charity'}
          </button>
        </div>

        {error && <p className="text-red-500 bg-red-100 p-3 rounded-md mb-4">{error}</p>}
        {successMessage && <p className="text-green-500 bg-green-100 p-3 rounded-md mb-4">{successMessage}</p>}

        {/* Charity details (read-only — there is no charity update endpoint) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InfoItem label="Name" value={charity.name} />
          <InfoItem
            label="Website"
            value={charity.website
              ? <a href={charity.website} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{charity.website}</a>
              : 'N/A'}
          />
          <div className="md:col-span-2">
            <InfoItem label="Description" value={charity.description || 'N/A'} />
          </div>
          {charity.logo_url && (
            <div className="md:col-span-2">
              <h3 className="text-sm font-medium text-gray-500">Logo</h3>
              <img src={charity.logo_url} alt="Logo" className="mt-2 h-20 w-auto rounded-md object-contain border" onError={(e) => e.target.style.display = 'none'} />
            </div>
          )}
        </div>

        {/* Stripe Connect verification */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-gray-800">Stripe Connect</h2>
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              isPledgeable ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
            }`}>
              {isPledgeable ? 'Pledgeable' : 'Not pledgeable'}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InfoItem label="Stripe Account ID" value={charity.stripe_account_id || 'N/A'} />
            <InfoItem label="Verified" value={charity.is_stripe_verified ? 'Yes' : 'No'} />
            <InfoItem label="Charges Enabled" value={charity.stripe_charges_enabled ? 'Yes' : 'No'} />
            <InfoItem
              label="Last Verified"
              value={charity.stripe_last_verified_at ? new Date(charity.stripe_last_verified_at).toLocaleString() : 'Never'}
            />
          </div>

          <div className="mt-6 bg-gray-50 p-4 rounded-lg">
            <label htmlFor="overrideAccountId" className="block text-sm font-medium text-gray-700 mb-2">
              Re-verify Stripe account (optional override)
            </label>
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
              <div className="flex-1 w-full">
                <input
                  type="text"
                  id="overrideAccountId"
                  value={overrideAccountId}
                  onChange={(e) => setOverrideAccountId(e.target.value)}
                  placeholder={charity.stripe_account_id || 'acct_xxxxxxxxxxxxxxxx'}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Leave blank to re-verify the stored account, or enter a new
                  acct_ id to replace and verify it.
                </p>
              </div>
              <button
                onClick={handleVerifyStripe}
                disabled={verifying || loading}
                className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-6 rounded-lg transition duration-150 ease-in-out disabled:opacity-50"
              >
                {verifying ? 'Verifying...' : 'Verify Stripe Account'}
              </button>
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-semibold text-gray-800 mb-4 mt-10 pt-6 border-t border-gray-200">Pledges for this Charity</h2>
        {pledges.length > 0 ? (
           <div className="overflow-x-auto shadow-md rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 bg-white">
              <thead className="bg-gray-50">
                <tr>
                  {pledgeColumns.map(col => (
                    <th key={col.key} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {col.header}
                    </th>
                  ))}
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pledges.map(pledge => (
                  <tr key={pledge.id} className="hover:bg-gray-50 transition-colors">
                    {pledgeColumns.map(col => (
                      <td key={col.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {col.render ? col.render(pledge[col.key], pledge) : (pledge[col.key] === undefined || pledge[col.key] === null ? 'N/A' : String(pledge[col.key]))}
                      </td>
                    ))}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <Link to={`/pledges/${pledge.id}`} className="text-indigo-600 hover:text-indigo-900">View Pledge</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-600">No pledges found for this charity.</p>
        )}
      </div>
      <button
          onClick={() => navigate(-1)}
          className="mt-8 bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition duration-150 ease-in-out"
        >
          Back
      </button>

      {showConfirmModal && (
        <ConfirmModal
          isOpen={showConfirmModal}
          title="Confirm Charity Deletion"
          message={`Are you sure you want to delete charity "${charity.name}" (ID: ${charity.id})? This action cannot be undone.`}
          onConfirm={confirmDelete}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}
    </div>
  );
};

const InfoItem = ({ label, value }) => (
  <div>
    <h3 className="text-sm font-medium text-gray-500">{label}</h3>
    <p className="mt-1 text-md text-gray-900 break-words">{value || 'N/A'}</p>
  </div>
);

export default CharityView;
